import React, { useState, useRef, useCallback, useEffect } from "react";
import { env } from "../../datas.config";
import {
  BsImage,
  BsCloudArrowUp,
  BsXCircleFill,
  BsPlusLg,
} from "react-icons/bs";
import { MdTextFields } from "react-icons/md";
import { IoColorPaletteOutline } from "react-icons/io5";
import { RxFontFamily, RxCross2 } from "react-icons/rx";

// ─── Dégradés prédéfinis ───
const BG_COLORS = [
  { key: "teal", gradient: "linear-gradient(135deg, #00b09b, #96c93d)" },
  { key: "ocean", gradient: "linear-gradient(135deg, #2193b0, #6dd5ed)" },
  {
    key: "night",
    gradient: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
  },
  { key: "purple", gradient: "linear-gradient(135deg, #667eea, #764ba2)" },
  { key: "rose", gradient: "linear-gradient(135deg, #ee9ca7, #ffdde1)" },
  { key: "sunset", gradient: "linear-gradient(135deg, #f12711, #f5af19)" },
  { key: "forest", gradient: "linear-gradient(135deg, #134e5e, #71b280)" },
  { key: "wine", gradient: "linear-gradient(135deg, #4a0e0e, #b91d73)" },
  {
    key: "midnight",
    gradient: "linear-gradient(135deg, #0f0c29, #302b63, #24243e)",
  },
  { key: "peach", gradient: "linear-gradient(135deg, #ff9966, #ff5e62)" },
  { key: "sky", gradient: "linear-gradient(135deg, #56ccf2, #2f80ed)" },
  { key: "charcoal", gradient: "linear-gradient(135deg, #232526, #414345)" },
];

// ─── Polices ───
const FONTS = [
  {
    key: "sans",
    family: "'Segoe UI', 'Helvetica Neue', sans-serif",
    name: "Sans-serif",
  },
  {
    key: "serif",
    family: "'Georgia', 'Times New Roman', serif",
    name: "Serif",
  },
  {
    key: "mono",
    family: "'Fira Code', 'Courier New', monospace",
    name: "Mono",
  },
  {
    key: "cursive",
    family: "'Dancing Script', 'Brush Script MT', cursive",
    name: "Cursive",
  },
  { key: "display", family: "'Impact', 'Anton', sans-serif", name: "Display" },
  {
    key: "rounded",
    family: "'Nunito', 'Varela Round', sans-serif",
    name: "Rounded",
  },
  {
    key: "elegant",
    family: "'Playfair Display', 'Didot', serif",
    name: "Elegant",
  },
  {
    key: "handwriting",
    family: "'Caveat', 'Comic Sans MS', cursive",
    name: "Manuscrit",
  },
  {
    key: "condensed",
    family: "'Roboto Condensed', 'Arial Narrow', sans-serif",
    name: "Condensé",
  },
  { key: "slab", family: "'Roboto Slab', 'Rockwell', serif", name: "Slab" },
  {
    key: "futuristic",
    family: "'Orbitron', 'Eurostile', sans-serif",
    name: "Futuriste",
  },
  {
    key: "typewriter",
    family: "'Special Elite', 'Courier', monospace",
    name: "Machine",
  },
];

/**
 * Résout le fond d'une annonce.
 * - Si key matche un preset → renvoie le gradient preset
 * - Si key commence par "custom:" → c'est un gradient personnalisé stocké directement
 * - Sinon fallback sur purple
 */
export function getBgGradient(key) {
  if (!key) return BG_COLORS[3].gradient;
  if (key.startsWith("custom:")) return key.slice(7);
  return (
    BG_COLORS.find((c) => c.key === key)?.gradient || BG_COLORS[3].gradient
  );
}

export function getFontFamily(key) {
  return FONTS.find((f) => f.key === key)?.family || FONTS[0].family;
}

// ─── Style pour cacher l'input color natif et le rendre cliquable ───
const colorSwatchStyle = (color) => ({
  position: "relative",
  width: 40,
  height: 40,
  borderRadius: 10,
  background: color,
  border: "3px solid #fff",
  boxShadow: "0 2px 8px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(0,0,0,0.08)",
  cursor: "pointer",
  overflow: "hidden",
  transition: "transform 0.15s ease, box-shadow 0.15s ease",
  flexShrink: 0,
});

const hiddenColorInputStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  opacity: 0,
  cursor: "pointer",
  border: "none",
  padding: 0,
};

// ─── Composant interne : Sélecteur de dégradé personnalisé ───
function CustomGradientPicker({ color1, color2, angle, onChange }) {
  const previewGradient = `linear-gradient(${angle}deg, ${color1}, ${color2})`;

  return (
    <div
      style={{
        background: "#f8f9fa",
        borderRadius: 14,
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      {/* Aperçu du dégradé */}
      <div
        style={{
          background: previewGradient,
          height: 32,
          borderRadius: 8,
          boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
        }}
      />

      {/* Sélecteurs de couleurs */}
      <div className="d-flex align-items-center gap-3">
        <div className="d-flex align-items-center gap-2 flex-fill">
          <span style={{ fontSize: "0.8rem", color: "#6c757d" }}>Début</span>
          <div
            style={colorSwatchStyle(color1)}
            title="Choisir la couleur de début"
          >
            <input
              type="color"
              value={color1}
              onChange={(e) => onChange({ color1: e.target.value })}
              style={hiddenColorInputStyle}
            />
          </div>
          <span style={{ fontSize: "0.72rem", color: "#adb5bd", fontFamily: "monospace" }}>{color1}</span>
        </div>
        <div className="d-flex align-items-center gap-2 flex-fill">
          <span style={{ fontSize: "0.8rem", color: "#6c757d" }}>Fin</span>
          <div
            style={colorSwatchStyle(color2)}
            title="Choisir la couleur de fin"
          >
            <input
              type="color"
              value={color2}
              onChange={(e) => onChange({ color2: e.target.value })}
              style={hiddenColorInputStyle}
            />
          </div>
          <span style={{ fontSize: "0.72rem", color: "#adb5bd", fontFamily: "monospace" }}>{color2}</span>
        </div>
      </div>

      {/* Slider d'angle */}
      <div className="d-flex align-items-center gap-2">
        <span style={{ fontSize: "0.8rem", color: "#6c757d", whiteSpace: "nowrap" }}>Direction</span>
        <input
          type="range"
          min="0"
          max="360"
          value={angle}
          onChange={(e) => onChange({ angle: parseInt(e.target.value) })}
          className="form-range flex-fill"
          style={{ height: 6 }}
        />
        <span
          style={{
            fontSize: "0.75rem",
            color: "#6c757d",
            minWidth: 32,
            textAlign: "right",
            fontFamily: "monospace",
          }}
        >
          {angle}°
        </span>
      </div>
    </div>
  );
}

export default function CreateAnnonceModal({ show, onClose, onCreated }) {
  const [type, setType] = useState("texte");
  const [contenu, setContenu] = useState("");
  const [legende, setLegende] = useState("");
  const [duree, setDuree] = useState("24h");
  const [bgColor, setBgColor] = useState("purple");
  const [fontFamily, setFontFamily] = useState("sans");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Personnalisation couleur
  const [showCustomColor, setShowCustomColor] = useState(false);
  const [customColor1, setCustomColor1] = useState("#667eea");
  const [customColor2, setCustomColor2] = useState("#764ba2");
  const [customAngle, setCustomAngle] = useState(135);

  // Animation d'entrée/sortie
  const [visible, setVisible] = useState(false);
  const [animClass, setAnimClass] = useState("");

  useEffect(() => {
    if (show) {
      setVisible(true);
      setAnimClass("animate__animated animate__backInDown");
    }
  }, [show]);

  const handleClose = () => {
    setAnimClass("animate__animated animate__backOutUp");
    setTimeout(() => {
      setVisible(false);
      setAnimClass("");
      resetForm();
      onClose();
    }, 500);
  };

  const customGradient = `linear-gradient(${customAngle}deg, ${customColor1}, ${customColor2})`;
  const isCustom = bgColor.startsWith("custom:");

  const handleImageChange = (file) => {
    if (!file) return;
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];
    if (!validTypes.includes(file.type)) {
      setError("Format d'image non supporté (JPG, PNG, GIF, WebP)");
      return;
    }
    setError("");
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleImageChange(file);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resetForm = () => {
    setType("texte");
    setContenu("");
    setLegende("");
    setDuree("24h");
    setBgColor("purple");
    setFontFamily("sans");
    setImageFile(null);
    setImagePreview(null);
    setError("");
    setIsDragging(false);
    setShowCustomColor(false);
    setCustomColor1("#667eea");
    setCustomColor2("#764ba2");
    setCustomAngle(135);
  };

  const applyCustomGradient = () => {
    setBgColor(`custom:${customGradient}`);
  };

  const handleCustomChange = (updates) => {
    if (updates.color1 !== undefined) setCustomColor1(updates.color1);
    if (updates.color2 !== undefined) setCustomColor2(updates.color2);
    if (updates.angle !== undefined) setCustomAngle(updates.angle);
    // Appliquer en temps réel
    const c1 = updates.color1 ?? customColor1;
    const c2 = updates.color2 ?? customColor2;
    const a = updates.angle ?? customAngle;
    setBgColor(`custom:linear-gradient(${a}deg, ${c1}, ${c2})`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let response;
      if (type === "image") {
        if (!imageFile) {
          setError("Veuillez sélectionner une image");
          setLoading(false);
          return;
        }
        const formData = new FormData();
        formData.append("type", "image");
        formData.append("duree", duree);
        formData.append("image", imageFile);
        formData.append("legende", legende);

        response = await fetch(`${env.domain}/api/auth/annonces`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });
      } else {
        if (!contenu.trim()) {
          setError("Veuillez saisir un contenu");
          setLoading(false);
          return;
        }
        response = await fetch(`${env.domain}/api/auth/annonces`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "texte",
            duree,
            contenu,
            bgColor,
            fontFamily,
          }),
        });
      }

      const data = await response.json();
      if (!response.ok) {
        setError(data.msg || "Erreur lors de la création");
        setLoading(false);
        return;
      }

      onCreated(data.annonce);
      // Animation de sortie puis fermeture
      setAnimClass("animate__animated animate__backOutUp");
      setTimeout(() => {
        setVisible(false);
        setAnimClass("");
        resetForm();
        onClose();
      }, 500);
    } catch (err) {
      console.error("Erreur création annonce:", err);
      setError("Erreur réseau");
    }
    setLoading(false);
  };

  if (!show && !visible) return null;

  const currentGradient = getBgGradient(bgColor);
  const currentFont = getFontFamily(fontFamily);

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1060 }}
      onClick={handleClose}
    >
      <div
        className={`modal-dialog modal-dialog-centered modal-lg ${animClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content border-0" style={{ overflow: "visible", position: "relative" }}>
          <button
            type="button"
            className="btn bg-danger text-white btn-md btn-icon btn-pill btn-white shadow position-absolute top-0 end-0 mt-n3 me-n3"
            style={{ zIndex: 10 }}
            onClick={handleClose}
          >
            <RxCross2 />
          </button>
          <div className="modal-body p-4">
            <h5 className="fw-bold mb-3">Nouvelle annonce</h5>
            <form onSubmit={handleSubmit}>
              <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
                {error && (
                  <div className="alert alert-danger py-2 mb-3" role="alert">
                    {error}
                  </div>
                )}

                {/* Choix du type */}
                <div className="d-flex gap-2 mb-3">
                  <button
                    type="button"
                    className={`btn flex-fill d-flex align-items-center justify-content-center gap-2 ${
                      type === "texte" ? "btn-primary" : "btn-outline-secondary"
                    }`}
                    onClick={() => setType("texte")}
                  >
                    <MdTextFields size={18} /> Texte
                  </button>
                  <button
                    type="button"
                    className={`btn flex-fill d-flex align-items-center justify-content-center gap-2 ${
                      type === "image" ? "btn-primary" : "btn-outline-secondary"
                    }`}
                    onClick={() => setType("image")}
                  >
                    <BsImage size={16} /> Image
                  </button>
                </div>

                {/* ─── TYPE TEXTE ─── */}
                {type === "texte" && (
                  <>
                    {/* Aperçu en direct */}
                    <div
                      style={{
                        background: currentGradient,
                        borderRadius: 12,
                        padding: "32px 24px",
                        minHeight: 160,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 16,
                        transition: "background 0.3s ease",
                      }}
                    >
                      <p
                        style={{
                          color: "#fff",
                          fontFamily: currentFont,
                          fontSize: contenu.length > 100 ? "1rem" : "1.25rem",
                          textAlign: "center",
                          lineHeight: 1.6,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          margin: 0,
                          textShadow: "0 1px 3px rgba(0,0,0,0.3)",
                        }}
                      >
                        {contenu || "Votre annonce s'affichera ici..."}
                      </p>
                    </div>

                    {/* Textarea */}
                    <textarea
                      className="form-control mb-3"
                      rows="3"
                      placeholder="Saisissez votre annonce..."
                      value={contenu}
                      onChange={(e) => setContenu(e.target.value)}
                      style={{ resize: "none" }}
                    />

                    {/* ── Couleur de fond ── */}
                    <div className="mb-3">
                      <label className="form-label d-flex align-items-center gap-2 mb-2">
                        <IoColorPaletteOutline size={18} /> Couleur de fond
                      </label>
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        {BG_COLORS.map((color) => {
                          const selected = bgColor === color.key;
                          return (
                            <button
                              key={color.key}
                              type="button"
                              onClick={() => {
                                setBgColor(color.key);
                                setShowCustomColor(false);
                              }}
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                background: color.gradient,
                                border: selected
                                  ? "3px solid #fff"
                                  : "2px solid transparent",
                                boxShadow: selected
                                  ? "0 0 0 2px #0d6efd"
                                  : "0 1px 3px rgba(0,0,0,0.2)",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                transform: selected
                                  ? "scale(1.15)"
                                  : "scale(1)",
                                padding: 0,
                                flexShrink: 0,
                              }}
                              title={color.key}
                            />
                          );
                        })}
                        {/* Bouton personnaliser */}
                        <button
                          type="button"
                          onClick={() => {
                            setShowCustomColor(!showCustomColor);
                            if (!showCustomColor && !isCustom) {
                              // Appliquer le custom par défaut
                              setBgColor(`custom:${customGradient}`);
                            }
                          }}
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: isCustom
                              ? currentGradient
                              : "conic-gradient(#f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)",
                            border: isCustom
                              ? "3px solid #fff"
                              : "2px solid transparent",
                            boxShadow: isCustom
                              ? "0 0 0 2px #0d6efd"
                              : "0 1px 3px rgba(0,0,0,0.2)",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            transform: isCustom ? "scale(1.15)" : "scale(1)",
                            padding: 0,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                          title="Personnaliser"
                        >
                          {!isCustom && (
                            <BsPlusLg
                              size={12}
                              style={{
                                color: "#fff",
                                filter:
                                  "drop-shadow(0 1px 2px rgba(0,0,0,0.5))",
                              }}
                            />
                          )}
                        </button>
                      </div>

                      {/* Panneau de personnalisation */}
                      {showCustomColor && (
                        <div style={{ marginTop: 10 }}>
                          <CustomGradientPicker
                            color1={customColor1}
                            color2={customColor2}
                            angle={customAngle}
                            onChange={handleCustomChange}
                          />
                        </div>
                      )}
                    </div>

                    {/* ── Sélecteur de police ── */}
                    <div className="mb-3">
                      <label className="form-label d-flex align-items-center gap-2 mb-2">
                        <RxFontFamily size={18} /> Police
                      </label>
                      <div className="d-flex flex-wrap gap-2">
                        {FONTS.map((font) => (
                          <button
                            key={font.key}
                            type="button"
                            onClick={() => setFontFamily(font.key)}
                            className={`btn btn-sm ${
                              fontFamily === font.key
                                ? "btn-dark"
                                : "btn-outline-secondary"
                            }`}
                            style={{
                              fontFamily: font.family,
                              minWidth: 64,
                              fontSize: "0.82rem",
                              padding: "4px 10px",
                            }}
                          >
                            {font.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* ─── TYPE IMAGE ─── */}
                {type === "image" && (
                  <>
                    {!imagePreview ? (
                      <div
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                          border: `2px dashed ${isDragging ? "#0d6efd" : "#ced4da"}`,
                          borderRadius: 16,
                          padding: "40px 20px",
                          textAlign: "center",
                          cursor: "pointer",
                          background: isDragging
                            ? "rgba(13,110,253,0.05)"
                            : "rgba(0,0,0,0.02)",
                          transition: "all 0.2s ease",
                          marginBottom: 16,
                        }}
                      >
                        <BsCloudArrowUp
                          size={48}
                          style={{
                            color: isDragging ? "#0d6efd" : "#adb5bd",
                            marginBottom: 12,
                          }}
                        />
                        <p
                          style={{
                            color: "#6c757d",
                            margin: "0 0 4px 0",
                            fontWeight: 500,
                          }}
                        >
                          Glissez une image ici
                        </p>
                        <p
                          style={{
                            color: "#adb5bd",
                            margin: 0,
                            fontSize: "0.85rem",
                          }}
                        >
                          ou cliquez pour parcourir
                        </p>
                        <p
                          style={{
                            color: "#adb5bd",
                            margin: "8px 0 0 0",
                            fontSize: "0.75rem",
                          }}
                        >
                          JPG, PNG, GIF, WebP
                        </p>
                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          accept="image/jpg,image/jpeg,image/png,image/gif,image/webp"
                          onChange={(e) => handleImageChange(e.target.files[0])}
                        />
                      </div>
                    ) : (
                      <div style={{ position: "relative", marginBottom: 16 }}>
                        <div
                          style={{
                            borderRadius: 16,
                            overflow: "hidden",
                            position: "relative",
                            maxHeight: 300,
                          }}
                        >
                          <img
                            src={imagePreview}
                            alt="Aperçu"
                            style={{
                              width: "100%",
                              maxHeight: 300,
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: 60,
                              background:
                                "linear-gradient(transparent, rgba(0,0,0,0.4))",
                              borderRadius: "0 0 16px 16px",
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={removeImage}
                          style={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            background: "rgba(0,0,0,0.5)",
                            border: "none",
                            borderRadius: "50%",
                            width: 32,
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "#fff",
                            backdropFilter: "blur(4px)",
                          }}
                          title="Retirer l'image"
                        >
                          <BsXCircleFill size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          style={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            background: "rgba(0,0,0,0.5)",
                            border: "none",
                            borderRadius: 20,
                            padding: "4px 12px",
                            cursor: "pointer",
                            color: "#fff",
                            fontSize: "0.8rem",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          Changer
                        </button>
                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          accept="image/jpg,image/jpeg,image/png,image/gif,image/webp"
                          onChange={(e) => handleImageChange(e.target.files[0])}
                        />
                      </div>
                    )}

                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Ajouter une légende (optionnel)..."
                        value={legende}
                        onChange={(e) => setLegende(e.target.value)}
                      />
                    </div>
                  </>
                )}

                {/* Durée */}
                <div className="mb-2">
                  <label className="form-label mb-2">Durée de l'annonce</label>
                  <div className="d-flex gap-2">
                    {[
                      { value: "24h", label: "24 heures" },
                      { value: "1semaine", label: "1 semaine" },
                      { value: "1mois", label: "1 mois" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`btn btn-sm flex-fill ${
                          duree === opt.value
                            ? "btn-dark"
                            : "btn-outline-secondary"
                        }`}
                        onClick={() => setDuree(opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={handleClose}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading ? "Publication..." : "Publier"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
